/**
 * Admin leak detail editor (/admin/leaks/:id).
 *
 * Edits status (4 options), resolution comment, can upload/delete images on
 * either side (initial or resolution kind). Includes delete-article button.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  LinkSimple,
  Trash,
  UploadSimple,
  CheckCircle,
  FloppyDisk,
} from "@phosphor-icons/react";
import { leaksApi, leaksAdminApi } from "../../api/leaks";
import LeakStatusBadge from "../../components/leaks/LeakStatusBadge";
import ConfirmDialog from "../../components/courses/ConfirmDialog";

const STATUS_OPTIONS = ["received", "in_progress", "success", "failure"];
const AUTOSAVE_MS = 900;

function ImageGrid({ images, onRemove, readOnly }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {images.map((img) => (
        <div
          key={img.id}
          className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-50 group"
        >
          <img
            src={leaksApi.imageUrl(img.id)}
            alt={img.original_filename}
            className="h-full w-full object-cover"
          />
          {!readOnly && (
            <button
              type="button"
              onClick={() => onRemove(img.id)}
              aria-label="Supprimer"
              data-testid={`admin-leak-image-remove-${img.id}`}
              className="absolute top-2 right-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/95 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Trash size={13} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AdminLeakDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [leak, setLeak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [comment, setComment] = useState("");
  const [saveStatus, setSaveStatus] = useState("idle");
  const [savedAt, setSavedAt] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const initialFileRef = useRef(null);
  const resolutionFileRef = useRef(null);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await leaksAdminApi.get(id);
      setLeak(data);
      setComment(data.resolution_comment || "");
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const persistComment = useCallback(
    async (next) => {
      if (!leak) return;
      setSaveStatus("saving");
      try {
        const updated = await leaksAdminApi.update(leak.id, {
          resolution_comment: next,
        });
        setLeak((l) => ({ ...l, ...updated }));
        setSaveStatus("saved");
        setSavedAt(new Date());
      } catch {
        setSaveStatus("error");
      }
    },
    [leak],
  );

  const onCommentChange = useCallback(
    (e) => {
      const v = e.target.value;
      setComment(v);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => persistComment(v), AUTOSAVE_MS);
    },
    [persistComment],
  );

  const setStatus = useCallback(
    async (newStatus) => {
      if (!leak || newStatus === leak.status) return;
      const updated = await leaksAdminApi.update(leak.id, { status: newStatus });
      setLeak((l) => ({ ...l, ...updated }));
    },
    [leak],
  );

  const uploadImage = useCallback(
    async (e, kind) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file || !leak) return;
      await leaksAdminApi.uploadImage(leak.id, file, kind);
      await load();
    },
    [leak, load],
  );

  const removeImage = useCallback(
    async (imgId) => {
      if (!leak) return;
      await leaksAdminApi.removeImage(leak.id, imgId);
      await load();
    },
    [leak, load],
  );

  const onDelete = useCallback(async () => {
    if (!leak) return;
    await leaksAdminApi.remove(leak.id);
    setConfirmDelete(false);
    navigate("/admin/leaks");
  }, [leak, navigate]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (loading) return <p className="text-sm text-slate-500">{t("common.loading")}</p>;
  if (error || !leak) {
    return <p className="text-sm text-red-600">{t("leaks.not_found")}</p>;
  }
  const initialImgs = leak.images.filter((i) => i.kind === "initial");
  const resolutionImgs = leak.images.filter((i) => i.kind === "resolution");
  const ownerLabel = leak.owner
    ? `${leak.owner.first_name || leak.owner.email} (${leak.owner.email})`
    : leak.owner_id;

  return (
    <div data-testid="admin-leak-detail" className="space-y-8 max-w-3xl">
      <Link
        to="/admin/leaks"
        data-testid="admin-leak-back"
        className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft size={14} weight="bold" />
        {t("admin.nav.leaks")}
      </Link>

      <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="space-y-2">
          <LeakStatusBadge status={leak.status} />
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
            {leak.title}
          </h1>
          <p className="text-sm text-slate-600">
            <span className="text-slate-400">{t("leaks.owner")}:</span> {ownerLabel}
          </p>
          <a
            href={leak.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 break-all"
          >
            <LinkSimple size={14} />
            {leak.url}
          </a>
        </div>
        <button
          type="button"
          onClick={() => setConfirmDelete(true)}
          data-testid="admin-leak-delete"
          className="inline-flex items-center justify-center rounded-full bg-red-50 text-red-600 hover:bg-red-100 h-10 w-10"
          aria-label={t("common.delete")}
        >
          <Trash size={15} weight="bold" />
        </button>
      </header>

      {leak.description && (
        <section className="rounded-2xl border border-slate-200 bg-white px-5 py-4">
          <p className="text-sm text-slate-700 whitespace-pre-wrap">
            {leak.description}
          </p>
        </section>
      )}

      {/* Initial images (read-only after creation) */}
      <section data-testid="admin-leak-initial-images" className="space-y-3">
        <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
          {t("leaks.attached_images")}
        </h2>
        {initialImgs.length === 0 ? (
          <p className="text-xs text-slate-400">{t("leaks.no_image")}</p>
        ) : (
          <ImageGrid images={initialImgs} readOnly />
        )}
      </section>

      {/* Status changer */}
      <section
        data-testid="admin-leak-status-section"
        className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3"
      >
        <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
          {t("leaks.change_status")}
        </h2>
        <div className="flex flex-wrap gap-2">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              data-testid={`admin-leak-set-${s}`}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition-colors ${
                leak.status === s
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {leak.status === s && <CheckCircle size={12} weight="fill" />}
              {t(`leaks.status.${s}`)}
            </button>
          ))}
        </div>
      </section>

      {/* Resolution note */}
      <section data-testid="admin-leak-resolution-section" className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
            {t("leaks.resolution_note")}
          </h2>
          <span
            data-testid="admin-leak-save-status"
            className="text-xs text-slate-500 inline-flex items-center gap-1.5"
          >
            {saveStatus === "saving" && (
              <>
                <FloppyDisk size={12} className="animate-pulse" />
                {t("admin.courses.saving")}
              </>
            )}
            {saveStatus === "saved" && savedAt && (
              <>
                <CheckCircle size={12} weight="fill" className="text-emerald-500" />
                {t("admin.courses.saved_at", {
                  time: savedAt.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  }),
                })}
              </>
            )}
            {saveStatus === "error" && (
              <span className="text-red-600">{t("admin.courses.save_error")}</span>
            )}
          </span>
        </div>
        <textarea
          data-testid="admin-leak-comment"
          value={comment}
          onChange={onCommentChange}
          rows={4}
          placeholder={t("leaks.resolution_placeholder")}
          className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm resize-y"
        />

        {/* Resolution images */}
        <div className="flex items-center justify-between gap-3 pt-2">
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
            {t("leaks.resolution_images")}
          </span>
          <button
            type="button"
            onClick={() => resolutionFileRef.current?.click()}
            data-testid="admin-leak-add-resolution-image"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200"
          >
            <UploadSimple size={13} weight="bold" />
            {t("leaks.add_image")}
          </button>
          <input
            ref={resolutionFileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => uploadImage(e, "resolution")}
          />
        </div>
        {resolutionImgs.length === 0 ? (
          <p className="text-xs text-slate-400">{t("leaks.no_resolution_image")}</p>
        ) : (
          <ImageGrid images={resolutionImgs} onRemove={removeImage} />
        )}
      </section>

      <ConfirmDialog
        open={confirmDelete}
        title={t("leaks.delete_title")}
        description={t("leaks.delete_desc")}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        destructive
        onConfirm={onDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
